import React from 'react';
import {Box, Skeleton, Stack} from '@mui/material';
import type {ContentSkeletonVariant, PageShellLoading, PageShellSlot} from './types';

type ActiveProps = {
    active?: boolean;
};

type TitleSkeletonProps = ActiveProps & {
    subtitle?: boolean;
};

type ActionsSkeletonProps = ActiveProps & {
    count?: number;
};

type ContentSkeletonProps = ActiveProps & {
    variant?: ContentSkeletonVariant;
};

export const BackSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active ? <Skeleton variant="circular" width={32} height={32} sx={{mr: 0.5, flexShrink: 0}}/> : null
);

export const TitleSkeleton = ({active, subtitle}: TitleSkeletonProps): React.ReactElement | null => (
    active
        ? (
            <Stack spacing={0.75} sx={{flex: 1, minWidth: 0}}>
                <Skeleton variant="text" width="40%" height={32}/>
                {subtitle && <Skeleton variant="text" width="28%" height={20}/>}
            </Stack>
        )
        : null
);

export const BreadcrumbsSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active
        ? (
            <Box sx={{display: 'flex', gap: 1, mb: 2}}>
                <Skeleton variant="text" width={64} height={20}/>
                <Skeleton variant="text" width={12} height={20}/>
                <Skeleton variant="text" width={96} height={20}/>
            </Box>
        )
        : null
);

export const ActionsSkeleton = ({active, count = 2}: ActionsSkeletonProps): React.ReactElement | null => (
    active
        ? (
            <Box sx={{display: 'flex', gap: 1}}>
                {Array.from({length: count}).map((_, i) => (
                    <Skeleton key={i} variant="rounded" width={96} height={36}/>
                ))}
            </Box>
        )
        : null
);

export const AlertsSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active ? <Skeleton variant="rounded" width="100%" height={48} sx={{mb: 2}}/> : null
);

export const FiltersSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active ? <Skeleton variant="rounded" width="100%" height={88} sx={{mb: 3}}/> : null
);

export const TabsSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active
        ? (
            <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                <Skeleton variant="rounded" width={88} height={36}/>
                <Skeleton variant="rounded" width={88} height={36}/>
                <Skeleton variant="rounded" width={88} height={36}/>
            </Box>
        )
        : null
);

/**
 * Content-area placeholder; shape depends on page preset.
 */
export const ContentSkeleton = ({active, variant = 'list'}: ContentSkeletonProps): React.ReactElement | null => {
    if (!active) {
        return null;
    }
    if (variant === 'settings') {
        return (
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                {Array.from({length: 4}).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={120}/>
                ))}
            </Box>
        );
    }
    if (variant === 'detail') {
        return (
            <Stack spacing={2}>
                <Skeleton variant="rounded" width="100%" height={48}/>
                <Skeleton variant="rounded" width="100%" height={240}/>
            </Stack>
        );
    }
    return (
        <Stack spacing={1}>
            {Array.from({length: 6}).map((_, i) => (
                <Skeleton key={i} variant="rounded" width="100%" height={40}/>
            ))}
        </Stack>
    );
};

export const BottomBarSkeleton = ({active}: ActiveProps): React.ReactElement | null => (
    active
        ? (
            <Box sx={{position: 'sticky', bottom: 0, py: 1}}>
                <Skeleton variant="rounded" width="100%" height={56}/>
            </Box>
        )
        : null
);

/**
 * Normalizes loading prop to a per-slot boolean map.
 * Truthy → skeleton; `false` / `null` / `undefined` → real content.
 */
export const resolveSlotLoading = (
    loading: PageShellLoading,
    slots: readonly PageShellSlot[],
): Record<PageShellSlot, boolean> => {
    const map = {} as Record<PageShellSlot, boolean>;
    for (const slot of slots) {
        map[slot] = false;
    }
    if (loading == null || loading === false) {
        return map;
    }
    if (loading === true) {
        for (const slot of slots) {
            map[slot] = true;
        }
        return map;
    }
    for (const slot of slots) {
        map[slot] = Boolean(loading[slot]);
    }
    return map;
};
